import React, { useReducer, useEffect, useRef } from "react";
import { isMobile } from "react-device-detect";
import { Mousewheel, Navigation, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "components/swiper";
import { lazyLoadVideos } from "helpers/lazy-load-videos";
import FullScreenSlider from "./full-screen-slider.js";
import { ReactComponent as RightArrow } from "assets/svgs/right-arrow.svg";
import { ReactComponent as LeftArrow } from "assets/svgs/left-arrow.svg";
import { ReactComponent as Play } from "assets/svgs/play.svg";
import { ReactComponent as SoundOn } from "assets/svgs/sound-on.svg";
import { ReactComponent as SoundOff } from "assets/svgs/sound-off.svg";
import { ReactComponent as Close } from "assets/svgs/close.svg";
import { formatTitle } from "helpers/format-title";

const initialState = {
	isVideoPlaying: {},
	videoDurations: {},
	isFullScreen: false,
	startAtSlide: 0,
	areDesktopVideosMuted: true,
	areMobileVideosMuted: true,
	unmutedVideoIndex: -1,
};

const reducer = (state, action) => {
	switch (action.type) {
		case "SET_IS_VIDEO_PLAYING":
			return {
				...state,
				isVideoPlaying: { ...state.isVideoPlaying, [action.key]: action.value },
			};
		case "SET_VIDEO_DURATIONS":
			return {
				...state,
				videoDurations: { ...state.videoDurations, [action.key]: action.value },
			};
		case "SET_IS_FULL_SCREEN":
			return { ...state, isFullScreen: action.value };
		case "SET_START_AT_SLIDE":
			return { ...state, startAtSlide: action.value };
		case "SET_ARE_DESKTOP_VIDEOS_MUTED":
			return { ...state, areDesktopVideosMuted: action.value };
		case "SET_ARE_MOBILE_VIDEOS_MUTED":
			return { ...state, areMobileVideosMuted: action.value };
		case "SET_UNMUTED_VIDEO_INDEX":
			return { ...state, unmutedVideoIndex: action.value };
		default:
			return state;
	}
};

const settings = {
	modules: [Mousewheel, Navigation, Pagination],
	preventClicks: true,
	preventClicksPropagation: false,
	spaceBetween: 15,
	breakpoints: {
		1024: { slidesPerView: 4.3 },
		768: { slidesPerView: 3.3 },
		480: { slidesPerView: 2.3 },
		320: { slidesPerView: 1.3 },
	},
	slidesPerGroup: 1,
	mousewheel: {
		enabled: true,
		forceToAxis: true,
	},
	pagination: {
		clickable: true,
	},
	navigation: {
		enabled: true,
		nextEl: ".right-arrow-v",
		prevEl: ".left-arrow-v",
	},
};

const VideoSlider = ({ data }) => {
	const [state, dispatch] = useReducer(reducer, initialState);
	const videoRefs = useRef([]);
	const modalRef = useRef(null);

	if (videoRefs.current.length !== data.videos.length) {
		videoRefs.current = Array(data.videos.length).fill().map((_, i) => videoRefs.current[i] || React.createRef());
	}

	useEffect(() => {
		lazyLoadVideos();
	}, []);

	const formatDuration = (duration) => {
		if (duration >= 60) {
			return { minutes: Math.floor(duration / 60), seconds: Math.floor(duration % 60) };
		} else return Math.ceil(duration);
	};

	const formatSeconds = (seconds) => {
		return seconds > 9 ? seconds : `0${seconds}`;
	};

	const handleVideoPlay = (key, isPlaying, autoplay, muted) => {
		dispatch({ type: "SET_IS_VIDEO_PLAYING", key, value: isPlaying });
		if (isPlaying && !autoplay && !muted) {
			dispatch({ type: "SET_UNMUTED_VIDEO_INDEX", value: key });
			dispatch({ type: "SET_ARE_DESKTOP_VIDEOS_MUTED", value: false });
		}
	};

	const handleTimeUpdate = (e, index) => {
		const currentTime = e.target.currentTime;
		dispatch({ type: "SET_IS_VIDEO_PLAYING", key: index, value: currentTime });
	};

	const togglePlayPause = (index) => {
		const video = videoRefs.current[index].current;
		if (isMobile) {
			video.load();
			dispatch({ type: "SET_START_AT_SLIDE", value: index });
			dispatch({ type: "SET_ARE_MOBILE_VIDEOS_MUTED", value: state.unmutedVideoIndex !== index });
			dispatch({ type: "SET_ARE_DESKTOP_VIDEOS_MUTED", value: true });
			dispatch({ type: "SET_IS_FULL_SCREEN", value: true });
			return;
		}
		if (video) {
			if (video.paused) {
				video.play();
			} else {
				video.pause();
			}
		}
	};

	const handlePauseAndReset = (key) => {
		const videoElement = videoRefs.current[key].current;
		if (videoElement) {
			videoElement.autoplay = false;
			videoElement.pause();
			videoElement.load();
		}
	};

	return (
		<>
			<div className="w-11/12 lg:w-4/5 m-auto hd-screen-max-width">
				<div className="text-center mb-10">
					<h2>{formatTitle(data?.title)}</h2>
				</div>
				<div className="relative w-full lg:my-20ß m-auto hd-screen-max-width">
					<Swiper className="m-auto" {...settings}>
						{data.videos.map((slide, index) => (
							<SwiperSlide className="m-auto h-auto max-w-[100%] flex items-center" key={slide._key}>
								<video
									id={slide._key}
									autoPlay={slide.video.autoplay}
									muted={
										state.areDesktopVideosMuted ||
										slide.video.muted ||
										state.unmutedVideoIndex !== index
									}
									loop={true}
									playsInline
									ref={videoRefs.current[index]}
									poster={slide.thumbnail}
									onLoadedMetadata={(e) => {
										dispatch({ type: "SET_VIDEO_DURATIONS", key: index, value: formatDuration(e.target.duration) });
									}}
									onPlay={() => handleVideoPlay(index, true, slide.video.autoplay, slide.video.muted)}
									onPause={() => handleVideoPlay(index, false, slide.video.autoplay, slide.video.muted)}
									onClick={() => togglePlayPause(index)}
									onTimeUpdate={(e) => handleTimeUpdate(e, index)}
									className="rounded-[12px]"
								>
									<source src={slide.video.src} type="video/mp4" />
									Your browser does not support HTML5 video.
								</video>
								{state.videoDurations[index] && (
									<div className="absolute right-4 bottom-2 flex">
										{state.isVideoPlaying[index] ? (
											<>
												<h6>{formatSeconds(Math.floor(state.isVideoPlaying[index] / 60))}</h6>
												<h6>:</h6>
												<h6>{formatSeconds(Math.floor(state.isVideoPlaying[index] % 60))}</h6>
											</>
										) : (
											typeof state.videoDurations[index] === "object" ? (
												<>
													<h6>{state.videoDurations[index].minutes}</h6>
													<h6>:</h6>
													<h6>{state.videoDurations[index].seconds}</h6>
												</>
											) : (
												<>
													<h6>00</h6>
													<h6>:</h6>
													<h6>{formatSeconds(state.videoDurations[index])}</h6>
												</>
											)
										)}
									</div>
								)}
								{state.isVideoPlaying[index] ? (
									<div className="video-pause-reset-btn">
										<button
											onClick={() => handlePauseAndReset(index)}
											className="rounded-full p-3 bg-white bg-opacity-30 mb-2 cursor-pointer"
										>
											<Close width="22" height="22" fill="white" />
										</button>
										{!slide.video.muted ?
											state.unmutedVideoIndex === index ?
												(<button
													onClick={() => {
														dispatch({ type: "SET_ARE_DESKTOP_VIDEOS_MUTED", value: true });
														dispatch({ type: "SET_UNMUTED_VIDEO_INDEX", value: -1 });
													}}
													className="rounded-full bg-white bg-opacity-30 p-3 flex items-center cursor-pointer"
												>
													<SoundOn width="22" height="22" />
												</button>) :
												(<button
													onClick={() => {
														dispatch({ type: "SET_UNMUTED_VIDEO_INDEX", value: index });
														dispatch({ type: "SET_ARE_DESKTOP_VIDEOS_MUTED", value: false });
													}}
													className="rounded-full bg-white bg-opacity-30 p-3 flex items-center cursor-pointer"
												>
													<SoundOff width="22" height="22" />
												</button>)
											: <></>}
									</div>
								) : (
									<button onClick={() => togglePlayPause(index)} className="video-play-pause-btn">
										<Play />
									</button>
								)}
							</SwiperSlide>
						))}
						<div className="left-arrow-v">
							<LeftArrow />
						</div>
						<div className="right-arrow-v">
							<RightArrow />
						</div>
						<div className="swiper-pagination"></div>
					</Swiper >
				</div >
			</div >
			{isMobile && state.isFullScreen &&
				<FullScreenSlider ref={modalRef} sliderData={data.videos}
					startFrom={state.startAtSlide}
					cta={data.cta} setIsFullScreen={(value) => dispatch({ type: "SET_IS_FULL_SCREEN", value })}
					areMobileVideosMuted={state.areMobileVideosMuted}
					setUnmutedVideoIndex={(value) => dispatch({ type: "SET_UNMUTED_VIDEO_INDEX", value })}
					setAreDesktopVideosMuted={(value) => dispatch({ type: "SET_ARE_DESKTOP_VIDEOS_MUTED", value })}
				/>
			}
		</>
	);
};

export default VideoSlider;
